import { ApiNoteInProgress } from 'api/rtk-api'
import TickIcon from 'assets/tick-white.svg?react'
import clsx from 'clsx'
import { Tabs } from 'components/core-v2'
import { Navigate, useParams } from 'react-router-dom'

import { PATH } from '../../../const'
import { noteStatusTabMapper } from '../utilities/noteStatusTabMapper'
import { ActivateTab } from './activate-tab'
import classes from './activation-tab.module.scss'
import { ApproveTab } from './approve-tab'
import { CommitmentTab } from './commitment-tab'
import { SettlementTab } from './settlement-tab'

const ORDER = [
  'PendingApproval',
  'PendingCommitment',
  'PendingClientCommitment',
  'PendingSettlement',
  'PendingActivation',
  'Activating',
  'Expired',
  'Active'
]

interface TabsProps {
  inProgressNote: ApiNoteInProgress
}
const TopUpTabs = ({ inProgressNote }: TabsProps) => {
  const { activationTab: tab, noteId } = useParams()

  const isApproveComplete = ORDER.findIndex(status => status === inProgressNote.noteStatus) > 0
  const isCommitmentComplete = ORDER.findIndex(status => status === inProgressNote.noteStatus) > 2
  const isSettlementComplete = ORDER.findIndex(status => status === inProgressNote.noteStatus) > 3
  const isActivateComplete = ORDER.findIndex(status => status === inProgressNote.noteStatus) > 4
  return (
    <Tabs.List spacing="md">
      <Tabs.Tab
        className={clsx(classes.tab, classes.completedTab, classes.submitTab)}
        isSelected={false}
        value="submit"
      >
        <TickIcon />
        Submit
      </Tabs.Tab>
      <Tabs.Tab
        className={clsx(classes.tab, {
          [classes.completedTab]: isApproveComplete
        })}
        isSelected={tab === 'approve'}
        to={PATH.noteApprovalsSingleNote(noteId, 'activation', 'approve')}
        value="approve"
      >
        {isApproveComplete ? <TickIcon /> : '2.'} Approve
      </Tabs.Tab>
      <Tabs.Tab
        className={clsx(classes.tab, {
          [classes.completedTab]: isCommitmentComplete,
          [classes.readOnly]: !isApproveComplete
        })}
        isSelected={tab === 'commitment'}
        to={
          isApproveComplete
            ? PATH.noteApprovalsSingleNote(noteId, 'activation', 'commitment')
            : null
        }
        value="commitment"
      >
        {isCommitmentComplete ? <TickIcon /> : '3.'} Commitment
      </Tabs.Tab>
      <Tabs.Tab
        className={clsx(classes.tab, {
          [classes.completedTab]: isSettlementComplete,
          [classes.readOnly]: !isCommitmentComplete
        })}
        isSelected={tab === 'settlement'}
        to={
          isCommitmentComplete
            ? PATH.noteApprovalsSingleNote(noteId, 'activation', 'settlement')
            : null
        }
        value="settlement"
      >
        {isSettlementComplete ? <TickIcon /> : '4.'} Settlement
      </Tabs.Tab>
      <Tabs.Tab
        className={clsx(classes.tab, {
          [classes.completedTab]: isActivateComplete,
          [classes.readOnly]: !isSettlementComplete
        })}
        isSelected={tab === 'activate'}
        to={
          isSettlementComplete
            ? PATH.noteApprovalsSingleNote(noteId, 'activation', 'activate')
            : null
        }
        value="activate"
      >
        {isActivateComplete ? <TickIcon /> : '5.'} Activate
      </Tabs.Tab>
    </Tabs.List>
  )
}

const WithdrawTabs = ({ inProgressNote }: TabsProps) => {
  const { activationTab: tab, noteId } = useParams()

  const isCommitmentComplete = ORDER.findIndex(status => status === inProgressNote.noteStatus) > 2
  const isActivateComplete = ORDER.findIndex(status => status === inProgressNote.noteStatus) > 4
  return (
    <Tabs.List spacing="md">
      <Tabs.Tab
        className={clsx(classes.tab, classes.completedTab, classes.submitTab)}
        isSelected={false}
        value="submit"
      >
        <TickIcon />
        Submit
      </Tabs.Tab>
      <Tabs.Tab
        className={clsx(classes.tab, {
          [classes.completedTab]: isCommitmentComplete
        })}
        isSelected={tab === 'commitment'}
        to={PATH.noteApprovalsSingleNote(noteId, 'activation', 'commitment')}
        value="commitment"
      >
        {isCommitmentComplete ? <TickIcon /> : '2.'} Commitment
      </Tabs.Tab>
      <Tabs.Tab
        className={clsx(classes.tab, {
          [classes.completedTab]: isActivateComplete,
          [classes.readOnly]: !isCommitmentComplete
        })}
        isSelected={tab === 'activate'}
        to={
          isCommitmentComplete
            ? PATH.noteApprovalsSingleNote(noteId, 'activation', 'activate')
            : null
        }
        value="activate"
      >
        {isActivateComplete ? <TickIcon /> : '3.'} Activate
      </Tabs.Tab>
    </Tabs.List>
  )
}

interface ActivationTabProps {
  inProgressNote: ApiNoteInProgress
}
export const ActivationTab = ({ inProgressNote }: ActivationTabProps) => {
  const { activationTab, noteId } = useParams()

  if (!activationTab) {
    return (
      <Navigate
        to={PATH.noteApprovalsSingleNote(
          noteId,
          'activation',
          noteStatusTabMapper(inProgressNote.noteStatus)
        )}
      />
    )
  }

  return (
    <>
      {inProgressNote.ongoingInstructionType === 'Deposit' ? (
        <TopUpTabs inProgressNote={inProgressNote} />
      ) : (
        <WithdrawTabs inProgressNote={inProgressNote} />
      )}
      <div className={classes.tabPanel}>
        {activationTab === 'approve' && <ApproveTab inProgressNote={inProgressNote} />}
      </div>
      <div className={classes.tabPanel}>
        {activationTab === 'commitment' && <CommitmentTab inProgressNote={inProgressNote} />}
      </div>
      <div className={classes.tabPanel}>
        {activationTab === 'settlement' && <SettlementTab inProgressNote={inProgressNote} />}
      </div>
      <div className={classes.tabPanel}>
        {activationTab === 'activate' && <ActivateTab inProgressNote={inProgressNote} />}
      </div>
    </>
  )
}
